<template>
  <el-row class="early-bird-qualifications" :gutter="18">
    <el-col :span="22" :offset="1">
      <el-form ref="qualifications" class="qualifications" :model="form" :rules="rules">
        <h4 class="text-uppercased">Qualifications</h4>
        <p>Answer the following questions to determine if the client is eligible for the Early Bird program.</p>
        <el-form-item label="What is the Early Bird Amount?" prop="earlyBirdAmt"
          v-if="isAdmin || !hideFields.earlyBirdAmt">
          <el-input type="number" v-model="qualificationVm.earlyBirdAmt" placeholder="Early Bird Amount">
            <template slot="prepend"><i class="fa fa-dollar-sign"></i></template>
          </el-input>
        </el-form-item>
        <el-form-item label="What is the client's refund amount?" prop="refundAmt"
          v-if="isAdmin || !hideFields.refundAmt">
          <el-input type="number" v-model="qualificationVm.refundAmt" placeholder="Refund Amount">
            <template slot="prepend"><i class="fa fa-dollar-sign"></i></template>
          </el-input>
        </el-form-item>
        <el-form-item label="What is the client's refund type?" prop="refundType"
          v-if="isAdmin || !hideFields.refundType">
          <el-radio-group v-model="qualificationVm.refundType">
            <el-radio :label="$options.REFUND_TYPE.BANK_PRODUCT">{{
              $options.REFUND_TYPE_DESCRIPTION_MAP[$options.REFUND_TYPE.BANK_PRODUCT] }}</el-radio>
            <el-radio :label="$options.REFUND_TYPE.PAY_UP_FRONT">{{
              $options.REFUND_TYPE_DESCRIPTION_MAP[$options.REFUND_TYPE.PAY_UP_FRONT] }}</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item prop="fmsDebt">
          <template slot="label">
            Does the client have FMS Debt?
            <el-tooltip effect="dark" content="Call (800) 304-3107 to find out if client has FMS Debt."
              placement="bottom">
              <i class="fa fa-question-circle"></i>
            </el-tooltip>
          </template>
          <el-radio-group v-model="qualificationVm.fmsDebt">
            <el-radio :label="true">Yes</el-radio>
            <el-radio :label="false">No</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Does the client have any nieces or nephews listed as a dependent?" prop="riskyDependents"
          v-if="isAdmin || !hideFields.riskyDependents">
          <el-radio-group v-model="qualificationVm.riskyDependents">
            <el-radio :label="true">Yes</el-radio>
            <el-radio :label="false">No</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="Is the taxpayer 18 years of age or older?" prop="over21"
          v-if="isAdmin || !hideFields.over21">
          <el-radio-group v-model="qualificationVm.over21">
            <el-radio :label="true">Yes</el-radio>
            <el-radio :label="false">No</el-radio>
          </el-radio-group>
        </el-form-item>
      </el-form>
    </el-col>
    <el-col :span="22" :offset="1" class="align-center">
      <el-button type="primary" icon="el-icon-success" class="submit_btn" :loading="isLoading"
        @click="submitClicked">Submit
        Client Application</el-button>
      OR
      <el-button type="danger" class="ml-0" icon="el-icon-error" :loading="isLoading" @click="optoutClicked"
        plain>Optout of
        Early Bird Program</el-button>
    </el-col>
  </el-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import get from 'lodash/get';
import { Required } from '@/util/formValidators';
import { REFUND_TYPE, REFUND_TYPE_DESCRIPTION_MAP } from '@/enum/RefundTypes';
import { EarlyBirdQualification } from '@/model';

const isNullOrUndefined = value => value === undefined || value === null;

export default {
  name: 'eb-qualifications',
  REFUND_TYPE,
  REFUND_TYPE_DESCRIPTION_MAP,
  data() {
    return {
      qualificationVm: new EarlyBirdQualification(),
      isLoading: false,
      hideFields: {},
      rules: {
        earlyBirdAmt: [Required],
        refundAmt: [Required],
        refundType: [Required],
        fmsDebt: [{ type: 'boolean', ...Required }],
        riskyDependents: [{ type: 'boolean', ...Required }],
        over21: [{ type: 'boolean', ...Required }],
      },
    };
  },
  watch: {
    ebQualification: {
      immediate: true,
      handler(val) {
        this.qualificationVm = new EarlyBirdQualification(val);
      },
    },
  },
  methods: {
    ...mapActions('Client', [
      'setQualification',
      'submitQualification',
      'optoutOfEarlyBirdProgram',
    ]),
    async save() {
      try {
        await this.$refs.qualifications.validate();
      } catch (e) {
        console.warn('qualification form validation failed', e);
        throw new Error('Validation failed');
      }

      this.isLoading = true;

      try {
        await this.setQualification(this.qualificationVm);
        await this.submitQualification();
      } finally {
        this.isLoading = false;
      }
    },
    submitClicked() {
      this.save().then(() => {
        this.$emit('nextStep');
      }).catch((e) => {
        if (e.message !== 'Validation failed') {
          const errorMsg = get(e, 'response.data', 'An error occurred.');
          this.$notify.error({
            title: 'Error during Save',
            message: errorMsg,
            position: 'bottom-right',
          });
        }
      });
    },
    optoutClicked() {
      this.$confirm('Are you sure the client would like to OPT-OUT of the Early Bird Program?').then(() => {
        this.isLoading = true;
        this.optoutOfEarlyBirdProgram().then(() => {
          this.$emit('nextStep');
        }).finally(() => {
          this.isLoading = false;
        });
      }).catch(() => { });
    },
  },
  computed: {
    ...mapGetters('User', [
      'isAdmin',
    ]),
    ...mapGetters('Client', [
      'ebQualification',
    ]),
    form() {
      return {
        earlyBirdAmt: this.qualificationVm.earlyBirdAmt,
        refundAmt: this.qualificationVm.refundAmt,
        refundType: this.qualificationVm.refundType,
        fmsDebt: this.qualificationVm.fmsDebt,
        riskyDependents: this.qualificationVm.riskyDependents,
        over21: this.qualificationVm.over21,
      };
    },
  },
  mounted() {
    this.hideFields = {
      earlyBirdAmt: !isNullOrUndefined(this.qualificationVm.earlyBirdAmt),
      refundAmt: !isNullOrUndefined(this.qualificationVm.refundAmt),
      refundType: !isNullOrUndefined(this.qualificationVm.refundType),
      riskyDependents: !isNullOrUndefined(this.qualificationVm.riskyDependents),
      over21: !isNullOrUndefined(this.qualificationVm.over21),
    };
  },
};

</script>

<style lang="scss">
.early-bird-qualifications {
  .el-form-item {
    .el-form-item__content {
      max-width: 300px;
      display: inline-block;
    }
  }

  .submit_btn {
    font-size: 1.5em;
  }
}
</style>
